import React, { useState, useEffect, useRef } from "react";
import {
  m as motion,
  MAnimatePresence as AnimatePresence,
} from "../../shared/components/Motion";
import "../../components/Themes/styles/themes.css";
import { modalVariantsForThemes } from "shared/utils";
import { Toast } from "shared/components";
import { useAdmin } from "./AdminProvider";
import {
  useHomeData,
  useAboutData,
  useSkillsData,
  useQualificationData,
  useCertificatesData,
  useProjectsData,
  useContactData,
  useDefaultThemesData,
} from "shared/hooks";
import { useAdminHandlers } from "./hooks/useAdminHandlers";
import { useVersionHistory } from "./hooks/useVersionHistory";
import { buildAllSections, buildCacheInfoMap } from "./hooks/adminPanelUtils";
import {
  AdminModeToggle,
  UnsavedChangesSection,
  VersionHistorySection,
  CacheInfoSection,
  AuthSection,
} from "./components";

const AdminPanel = () => {
  // Admin context
  const {
    isAdmin,
    adminMode,
    adminPanelOpen,
    expandUnsavedChanges,
    toggleAdminMode,
    closeAdminPanel,
    hasUnsavedChanges,
    getChangesBySection,
    signInWithGoogle,
    publishSkillsDraft,
    publishCertificatesDraft,
    publishProjectsDraft,
    publishAboutDraft,
    publishContactDraft,
    publishQualificationDraft,
    publishHomeDraft,
    publishDefaultThemesDraft,
    discardSkillsDraft,
    discardCertificatesDraft,
    discardProjectsDraft,
    discardAboutDraft,
    discardContactDraft,
    discardQualificationDraft,
    discardHomeDraft,
    discardDefaultThemesDraft,
    publishAllDrafts,
    discardDrafts,
    signOutUser,
    deleteVersion,
    undoSkillsPublish,
    undoCertificatesPublish,
    undoProjectsPublish,
    undoAboutPublish,
    undoContactPublish,
    undoQualificationPublish,
    undoHomePublish,
    undoDefaultThemesPublish,
  } = useAdmin();

  // UI state - accordion: only one section expanded at a time
  const [expandedSection, setExpandedSection] = useState(
    expandUnsavedChanges ? "changes" : null,
  );

  const isChangesExpanded = expandedSection === "changes";
  const isVersionHistoryExpanded = expandedSection === "versions";
  const isCacheInfoExpanded = expandedSection === "cache";

  // Helper to toggle section (close if already open, open if closed)
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Get cache info from all sections
  const homeCacheInfo = useHomeData()?.cacheInfo;
  const aboutCacheInfo = useAboutData()?.cacheInfo;
  const skillsCacheInfo = useSkillsData()?.cacheInfo;
  const qualificationCacheInfo = useQualificationData()?.cacheInfo;
  const certificatesCacheInfo = useCertificatesData()?.cacheInfo;
  const projectsCacheInfo = useProjectsData()?.cacheInfo;
  const contactCacheInfo = useContactData()?.cacheInfo;
  const defaultThemesCacheInfo = useDefaultThemesData()?.cacheInfo;

  // Build cache info map
  const cacheInfoMap = buildCacheInfoMap({
    homeCacheInfo,
    aboutCacheInfo,
    skillsCacheInfo,
    qualificationCacheInfo,
    certificatesCacheInfo,
    projectsCacheInfo,
    contactCacheInfo,
    defaultThemesCacheInfo,
  });

  // Auto-expand unsaved changes accordion when editing modals save
  useEffect(() => {
    if (expandUnsavedChanges) {
      setExpandedSection("changes");
    }
  }, [expandUnsavedChanges]);

  // Get admin handlers
  const {
    publishingState,
    discardingState,
    isSigningIn,
    signInMessage,
    clearingCache,
    handlePublish,
    handleDiscard,
    handleUndo,
    handleDeleteVersion,
    handlePublishAll,
    handleDiscardAll,
    handleSignIn,
    handleSignOut,
    handleClearCacheAndRefresh,
  } = useAdminHandlers({
    publishAllDrafts,
    discardDrafts,
    signOutUser,
    deleteVersion,
    closeAdminPanel,
    hasUnsavedChanges,
    signInWithGoogle,
  });

  // Get version history
  const {
    getVersionHistory,
    loadingVersions,
    isCurrentPublishedVersion,
    refetchVersionHistory,
  } = useVersionHistory(isVersionHistoryExpanded);

  // Preload version history on app load if user is admin
  React.useEffect(() => {
    if (isAdmin) {
      // Fetch version history in background on page load
      // Pass false to try localStorage first, then Firebase if needed
      refetchVersionHistory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]); // Only depend on isAdmin - refetchVersionHistory is memoized

  // Build all sections array
  const changesBySection = getChangesBySection();
  const allSections = buildAllSections(changesBySection, {
    publishAboutDraft,
    discardAboutDraft,
    undoAboutPublish,
    publishContactDraft,
    discardContactDraft,
    undoContactPublish,
    publishQualificationDraft,
    discardQualificationDraft,
    undoQualificationPublish,
    publishHomeDraft,
    discardHomeDraft,
    undoHomePublish,
    publishSkillsDraft,
    discardSkillsDraft,
    undoSkillsPublish,
    publishCertificatesDraft,
    discardCertificatesDraft,
    undoCertificatesPublish,
    publishProjectsDraft,
    discardProjectsDraft,
    undoProjectsPublish,
    publishDefaultThemesDraft,
    discardDefaultThemesDraft,
    undoDefaultThemesPublish,
  });

  const sectionsWithChanges = allSections.filter(
    (section) => section.changes > 0,
  );

  // Wrap handlers to include state setters
  const handlePublishWrapper = async (section) => {
    await handlePublish(section, () => toggleSection("versions"));
    // Small delay to ensure Firestore has written the data
    await new Promise((resolve) => setTimeout(resolve, 500));
    // Refetch version history after publish (force from Firebase)
    if (refetchVersionHistory) {
      await refetchVersionHistory(true);
    }
  };

  const handleUndoWrapper = async (section, versionId) => {
    await handleUndo(section, versionId, () => toggleSection("changes"));
  };

  const panelRef = useRef(null);

  // Close on click outside the panel (same pattern as chatbot)
  useEffect(() => {
    if (!adminPanelOpen) return;
    const handlePointerDown = (e) => {
      // Let the AuthButton's own onClick handle toggle — don't double-fire
      if (e.target.closest(".auth-button")) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        closeAdminPanel();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [adminPanelOpen, closeAdminPanel]);

  const handlePublishAllWrapper = async () => {
    await handlePublishAll(() => toggleSection("versions"));
    // Small delay to ensure Firestore has written the data
    await new Promise((resolve) => setTimeout(resolve, 500));
    // Refetch version history after publish (force from Firebase)
    if (refetchVersionHistory) {
      await refetchVersionHistory(true);
    }
  };
  return (
    <AnimatePresence>
      {adminPanelOpen && (
        <>
          <div
            ref={panelRef}
            className="themes__model-adminShell"
            style={{
              top: "9.5rem",
              right: "0",
              maxHeight: "fit-content",
              position: "fixed",
              gap: "0.75rem",
              opacity: 1,
              display: "flex",
              justifyContent: "center",
              transform: "none",
              alignItems: "flex-end",
              flexDirection: "column",
              zIndex: 999,
              pointerEvents: "none",
            }}
          >
            <motion.div
              className="themes__model active-model"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariantsForThemes}
              style={{
                position: "static",
                marginRight: "4rem",
                pointerEvents: "auto",
              }}
            >
              {/* Admin Mode Toggle */}
              {isAdmin && (
                <AdminModeToggle
                  adminMode={adminMode}
                  toggleAdminMode={toggleAdminMode}
                />
              )}

              {/* Unsaved Changes Section */}
              {isAdmin && (
                <UnsavedChangesSection
                  isChangesExpanded={isChangesExpanded}
                  setIsChangesExpanded={() => toggleSection("changes")}
                  sectionsWithChanges={sectionsWithChanges}
                  allSections={allSections}
                  publishingState={publishingState}
                  discardingState={discardingState}
                  onPublish={handlePublishWrapper}
                  onDiscard={handleDiscard}
                  onPublishAll={handlePublishAllWrapper}
                  onDiscardAll={handleDiscardAll}
                />
              )}

              {/* Version History Section */}
              {isAdmin && (
                <VersionHistorySection
                  isVersionHistoryExpanded={isVersionHistoryExpanded}
                  setIsVersionHistoryExpanded={() => toggleSection("versions")}
                  allSections={allSections}
                  loadingVersions={loadingVersions}
                  getVersionHistory={getVersionHistory}
                  isCurrentPublishedVersion={isCurrentPublishedVersion}
                  onUndo={handleUndoWrapper}
                  onDeleteVersion={handleDeleteVersion}
                  refetchVersionHistory={refetchVersionHistory}
                />
              )}

              {/* Cache Info Section */}
              <CacheInfoSection
                isCacheInfoExpanded={isCacheInfoExpanded}
                setIsCacheInfoExpanded={() => toggleSection("cache")}
                cacheInfoMap={cacheInfoMap}
                allSections={allSections}
                clearingCache={clearingCache}
                isAdmin={isAdmin}
                onClearCache={handleClearCacheAndRefresh}
              />

              {/* Auth Section */}
              <AuthSection
                isAdmin={isAdmin}
                isSigningIn={isSigningIn}
                onSignIn={handleSignIn}
                onSignOut={handleSignOut}
              />
            </motion.div>

            {/* Sign In Message Toast */}
            <Toast message={signInMessage} />
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AdminPanel;
